import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, defer } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface CompanyInfo {
  name: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  phone: string;
  email: string;
  bankDetails: string;
  taxNumber: string;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyInfoService {
  private readonly defaultCompanyInfo: CompanyInfo = {
    name: 'ProTyco UG',
    street: 'Alt Großziethen 12 A',
    postalCode: '12529',
    city: 'Schönefeld',
    country: 'Deutschland',
    phone: '+49 (0) 123 456789',
    email: 'info@protyco.de',
    bankDetails: '',
    taxNumber: 'DE123456789'
  };

  private readonly http = inject(HttpClient);

  public readonly companyInfo$: Observable<CompanyInfo> = defer(() =>
    this.loadCompanyInfo()
  ).pipe(
    shareReplay(1)
  );

  private loadCompanyInfo(): Observable<CompanyInfo> {
    return this.http.get<any[]>(`${environment.apiUrl}/config`).pipe(
      map(configs => {
        const configDict = this.buildConfigDict(configs);
        return {
          name: configDict['Company:Name'] || this.defaultCompanyInfo.name,
          street: configDict['Company:Street'] || this.defaultCompanyInfo.street,
          postalCode: configDict['Company:PostalCode'] || this.defaultCompanyInfo.postalCode,
          city: configDict['Company:City'] || this.defaultCompanyInfo.city,
          country: configDict['Company:Country'] || this.defaultCompanyInfo.country,
          phone: configDict['Company:Phone'] || this.defaultCompanyInfo.phone,
          email: configDict['Company:Email'] || this.defaultCompanyInfo.email,
          bankDetails: configDict['Company:BankDetails'] || this.defaultCompanyInfo.bankDetails,
          taxNumber: configDict['Company:TaxNumber'] || this.defaultCompanyInfo.taxNumber
        };
      }),
      catchError(error => {
        console.error('Error loading company info from config:', error);
        return of(this.defaultCompanyInfo);
      })
    );
  }

  private buildConfigDict(configs: any[]): Record<string, string> {
    const dict: Record<string, string> = {};
    if (Array.isArray(configs)) {
      configs.forEach(config => {
        if (config.key && config.value) {
          dict[config.key] = config.value;
        }
      });
    }
    return dict;
  }

  getCompanyInfo(): Observable<CompanyInfo> {
    return this.companyInfo$;
  }

  getFullAddress(): Observable<string> {
    return this.companyInfo$.pipe(
      map(info => `${info.street}, ${info.postalCode} ${info.city}, ${info.country}`)
    );
  }
}
